import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AnnouncementBar from '../components/AnnouncementBar';
import PasswordInput from '../components/PasswordInput';
import { payWithRazorpay, paymentStatus } from '../utils/razorpayCheckout';
import './PartnerPage.css';
import useResendTimer, { clock } from '../utils/useResendTimer';

// /partner: the Ownezzy partner (franchise) programme and its sign-up. The email is verified with the same emailed
// code as store sign-up (/api/onboarding/otp/*), which also takes the WhatsApp number. Then the partner pays for their first website bundle (the
// franchise plan's minimum, one month) in Razorpay: /api/partners/register makes the payment, and the partner account
// is made when it is paid (/api/billing/payments/:id says so).
const TITLE = 'Become an Ownezzy Partner | Earn 25% Commission Selling Websites';
const DESCRIPTION = 'Join the Ownezzy partner programme: sell ready-made online store websites to shops in your area at the partner rate and earn 25% commission on every website you sell.';

// The partner rate and the smallest bundle come from the live franchise plan (/api/plans); these are the defaults
const DEFAULT_RATE = { price: 299, minQuantity: 20 };
const rupees = (n) => `₹${Number(n).toLocaleString('en-IN')}`;

const benefitsFor = ({ price, minQuantity }) => [
  ['bi-cash-coin', '25% commission', 'Earn on every paid plan your customers buy, month after month.'],
  ['bi-shop', `${minQuantity} websites for ${rupees(price * minQuantity)}/month`, `${rupees(price)} per website per month at the partner rate (minimum ${minQuantity} websites). Create them from your partner panel.`],
  ['bi-link-45deg', 'Your referral link', 'Share any demo store with your link. Shops that sign up become your customers.'],
  ['bi-megaphone', 'Ready-made ads', 'Make banner ads for Instagram, WhatsApp and Facebook in a minute from the Marketing tab.'],
];

const stepsFor = ({ price, minQuantity }) => ['Sign up and verify your email', `Pay for your first ${minQuantity} websites (${rupees(price * minQuantity)} for a month)`, 'Share demo stores or create websites for shops', 'Earn 25% on every paid plan'];
const PAY_CHECK_MS = 5000;

async function post(url, body) {
  const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || (data.errors && data.errors[0] && data.errors[0].msg) || 'Something went wrong. Please try again.');
  return data;
}

function PartnerForm({ rate }) {
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', otp: '', terms: false });
  const [step, setStep] = useState('details'); // details -> code -> pay -> done
  const [order, setOrder] = useState(null); // { payment, payToken, checkout } from /api/partners/register
  const [paying, setPaying] = useState(false);
  const [token, setToken] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const resend = useResendTimer(30); // a new code 30 seconds after the last one
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const phone = form.phone.replace(/\D/g, '').slice(-10);

  const sendCode = async (e) => {
    e.preventDefault();
    setError('');
    if (form.name.trim().length < 2) return setError('Enter your full name.');
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return setError('Enter a valid email address.');
    if (form.password.length < 8) return setError('Choose a password of at least 8 characters.');
    if (phone.length !== 10) return setError('Enter your 10-digit WhatsApp number.');
    if (!form.terms) return setError('Please accept the Terms of Service and Privacy Policy.');
    setBusy(true);
    try {
      const data = await post('/api/onboarding/otp/send', { phone, email: form.email.trim(), for: 'partner' });
      setNote(data.devOtp ? `${data.message} (Development code: ${data.devOtp})` : data.message);
      setStep('code');
      resend.start();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const resendCode = async () => {
    setError('');
    try {
      const data = await post('/api/onboarding/otp/send', { phone, email: form.email.trim(), for: 'partner' });
      setNote(data.devOtp ? `${data.message} (Development code: ${data.devOtp})` : data.message);
      resend.start();
    } catch (err) {
      setError(err.message);
    }
  };

  const verifyAndRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(form.otp)) return setError('Enter the 6-digit code.');
    setBusy(true);
    try {
      const verificationToken = token || (await post('/api/onboarding/otp/verify', { phone, email: form.email.trim(), otp: form.otp })).verificationToken;
      setToken(verificationToken);
      // The email and WhatsApp number come from the verification, not from this request
      const data = await post('/api/partners/register', { name: form.name.trim(), password: form.password, verificationToken, termsAccepted: true });
      setOrder(data);
      if (data.payment.status === 'paid') await finishIfPaid(data.payment.id, data.payToken);
      else setStep('pay');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Once the payment is confirmed, its partner account is ready: show where to log in
  const finishIfPaid = async (paymentId, payToken) => {
    const payment = await paymentStatus(paymentId, payToken);
    if (payment.status === 'paid' && payment.partner) {
      setDone(payment.partner);
      setStep('done');
    }
    return payment;
  };

  const pay = async () => {
    setError('');
    if (!order.checkout) return setError('Online payment is not available right now. Please try again in a little while.');
    setPaying(true);
    try {
      const result = await payWithRazorpay(order.checkout);
      if (result) await finishIfPaid(order.payment.id, order.payToken);
    } catch (err) {
      setError(err.message);
    } finally {
      setPaying(false);
    }
  };

  // While waiting on the payment, check every few seconds: a payment finished in a UPI app is confirmed by Razorpay
  useEffect(() => {
    if (step !== 'pay' || !order) return undefined;
    const timer = setInterval(() => { finishIfPaid(order.payment.id, order.payToken).catch(() => {}); }, PAY_CHECK_MS);
    return () => clearInterval(timer);
  }, [step, order]);

  if (step === 'done' && done) {
    return (
      <div className="pp-form pp-done">
        <span className="pp-done-icon" aria-hidden="true">🎉</span>
        <h3>Welcome to the partner programme!</h3>
        <p>Payment received and your partner account is ready, with {done.websites} websites to create for your customers.</p>
        <div className="pp-done-card">
          <span>Admin panel</span><b>{done.adminUrl}</b>
          <span>Log in with</span><b>{done.login}</b> <small>and the password you chose</small>
          <span>Your referral code</span><b className="pp-code">{done.referralCode}</b>
        </div>
        <a className="pp-btn" href={`${done.adminUrl}/login`}>Open my partner panel →</a>
      </div>
    );
  }

  if (step === 'pay' && order) {
    const p = order.payment;
    return (
      <div className="pp-form">
        <h3>Pay for your first websites</h3>
        <p className="pp-form-sub">Your email is verified. Your partner account is made as soon as this payment goes through.</p>
        <div className="pp-bill">
          <span>{p.quantity} websites × {rupees(p.rate)} for 1 month</span><b>{rupees(p.baseAmount)}</b>
          {p.gstAmount > 0 && <><span>GST ({p.gstPercent}%)</span><b>{rupees(p.gstAmount)}</b></>}
          <strong>Total</strong><strong>{rupees(p.amount)}</strong>
        </div>
        {error && <p className="pp-error" role="alert">{error}</p>}
        {paying && <p className="pp-note">Waiting for the payment to be confirmed…</p>}
        <button className="pp-btn" onClick={pay} disabled={paying}>{paying ? 'Waiting for payment…' : `Pay ${rupees(p.amount)} by UPI`}</button>
        <p className="pp-form-sub pp-pay-note">You pay securely with Razorpay. You then log in with {form.email.trim()} and the password you chose.</p>
      </div>
    );
  }

  return (
    <form className="pp-form" onSubmit={step === 'details' ? sendCode : verifyAndRegister} noValidate>
      <h3>Create your partner account</h3>
      <p className="pp-form-sub">Verify your email, then pay for your first {rate.minQuantity} websites.</p>
      <label>Full name<input value={form.name} onChange={set('name')} autoComplete="name" disabled={step !== 'details'} /></label>
      <label>Email<input type="email" value={form.email} onChange={set('email')} autoComplete="email" disabled={step !== 'details'} /></label>
      <label>Password<PasswordInput value={form.password} onChange={set('password')} autoComplete="new-password" minLength={8} disabled={step !== 'details'} placeholder="At least 8 characters" /></label>
      <label>WhatsApp number
        <span className="pp-phone"><em>+91</em><input type="tel" inputMode="numeric" value={form.phone} onChange={set('phone')} autoComplete="tel-national" maxLength={14} disabled={step !== 'details'} placeholder="10-digit number" /></span>
      </label>
      {step === 'details' && (
        <label className="pp-terms"><input type="checkbox" checked={form.terms} onChange={set('terms')} /> <span>I agree to Ownezzy's <a href="/terms" target="_blank" rel="noreferrer">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a></span></label>
      )}
      {step === 'code' && (
        <>
          {note && <p className="pp-note">{note}</p>}
          <label>Code from your email<input className="pp-otp" inputMode="numeric" maxLength={6} value={form.otp} onChange={set('otp')} autoComplete="one-time-code" placeholder="••••••" autoFocus /></label>
        </>
      )}
      {error && <p className="pp-error" role="alert">{error}</p>}
      <button className="pp-btn" disabled={busy}>{busy ? 'Please wait…' : step === 'details' ? 'Email me a code' : 'Verify & continue to payment'}</button>
      {step === 'code' && <button type="button" className="pp-link" onClick={resendCode} disabled={resend.left > 0}>{resend.left > 0 ? `Resend code in ${clock(resend.left)}` : 'Resend code'}</button>}
      {step === 'code' && <button type="button" className="pp-link" onClick={() => { setStep('details'); setForm((f) => ({ ...f, otp: '' })); setToken(''); }}>← Change details</button>}
    </form>
  );
}

export default function PartnerPage() {
  const [rate, setRate] = useState(DEFAULT_RATE);
  useEffect(() => {
    let live = true;
    fetch('/api/plans')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const plan = data && Array.isArray(data.plans) && data.plans.find((p) => p.key === 'franchise');
        if (live && plan) setRate({ price: plan.price, minQuantity: plan.minQuantity || DEFAULT_RATE.minQuantity });
      })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  useEffect(() => {
    document.title = TITLE;
    const description = document.querySelector('meta[name="description"]');
    if (description) description.setAttribute('content', DESCRIPTION);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', 'https://ownezzy.com/partner');
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="app-shell">
      <AnnouncementBar />
      <Navbar />
      <main className="pp-page">
        <section className="pp-hero">
          <div className="container pp-hero-grid">
            <div>
              <span className="pp-kicker">OWNEZZY PARTNER PROGRAMME</span>
              <h1>Sell websites to shops. <span>Earn 25% commission.</span></h1>
              <p>Help shops in your city get their own online store. You bring the customers, Ownezzy builds and runs the websites, and you earn on every paid plan.</p>
              <ul className="pp-steps">
                {stepsFor(rate).map((s, i) => <li key={s}><b>{i + 1}</b>{s}</li>)}
              </ul>
            </div>
            <PartnerForm rate={rate} />
          </div>
        </section>
        <section className="pp-benefits container">
          {benefitsFor(rate).map(([icon, title, text]) => (
            <article key={title}>
              <i className={`bi ${icon}`} aria-hidden="true" />
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </section>
      </main>
      <Footer />
    </div>
  );
}
